import { useState, FC } from 'react'
import { useDispatch } from 'react-redux'
import { NavLink, Redirect, useLocation } from 'react-router-dom'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { Capacitor } from '@capacitor/core'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import { OpenAPI } from 'services/Api'
import { setCompany, setLoggedIn } from 'store/actions/auth'
import { Button, Form, Input } from 'components/Forms'
import { Card as MerinioCard } from 'components/UI'
import { Alert, Button as MerinioButton, Card } from 'ui'

type InitialValues = {
  company: string
}

const SelectCompany: FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation('legacy')
  const location = useLocation<{
    emailConfirmation: string
  }>()

  const [returnToLogin, setReturnToLogin] = useState(false)
  const [coInvalid, setCoInvalid] = useState<string>('')
  const [displayToken, setDisplayToken] = useState(0)
  const [token, setToken] = useState('')

  //Set the state of the confirmationAlert, default value is sent from ForgotCompany
  const [confirmationAlert, setConfirmationAlert] = useState(location.state?.emailConfirmation)

  const companyInURL = window.location.hostname.split('.')[0]
  const invalidCompany = !['api'].includes(companyInURL)

  const LoginSchema = Yup.object().shape({
    company: Yup.string().min(2, 'auth.TOO_SHORT').max(70, 'auth.TOO_LONG').required('auth.REQUIRED')
  })

  const onSubmit = async (values: InitialValues, actions: FormikHelpers<InitialValues>) => {
    const { company: userInput } = values

    try {
      const [company] = userInput
        .trim()
        .toLowerCase()
        .replace(/ /g, '')
        .replace(/(https:\/\/|http:\/\/)/g, '')
        .split('.')

      // Validate the company name
      const { success, url } = await OpenAPI.post('/lookup', { company })
      if (!success) return setCoInvalid(company)
      // If it is valid, set it in redux and redirect the user to login
      dispatch(setCompany(company))
      // In web app, redirect you to the url
      if (!Capacitor.isNativePlatform()) return window.location.replace(url)
      // In mobile app, set it in the store
      else setReturnToLogin(true)
    } catch (err) {
      const error = err as AxiosError

      actions.setSubmitting(false)
      actions.setStatus({ msg: error.message })
    }
  }

  const initialValues: InitialValues = {
    company: ''
  }
  // Redirect back to login page with the selected company
  // Web app
  if (returnToLogin) return <Redirect to={{ pathname: '/login' }} />

  return (
    <>
      <MerinioCard>
        <Card.Body>
          <Formik validationSchema={LoginSchema} initialValues={initialValues} onSubmit={onSubmit}>
            {({ status, isSubmitting, dirty, resetForm, setStatus }) => (
              <Form resetForm={resetForm} dirty={!!dirty && !isSubmitting}>
                {() => (
                  <>
                    <Alert id="select-company-alert" onClose={() => setStatus()} show={!!status?.msg}>
                      {status?.msg}
                    </Alert>
                    <Alert id="select-company-alert" show={coInvalid.length > 0}>
                      {t('auth.INVALID_COMPANY', { co: coInvalid })}
                    </Alert>
                    {/* Alert for confirmation that an email was sent to reset password or username*/}
                    <Alert variant="success" onClose={() => setConfirmationAlert('')} show={!!confirmationAlert}>
                      {t(`auth.EMAIL_${confirmationAlert}_CONFIRMATION`)}
                    </Alert>
                    <Alert id="select-company-alert" show={!Capacitor.isNativePlatform() && invalidCompany}>
                      {t('auth.NO_COMPANY_EXISTS_ON_URL', { co: companyInURL })}
                    </Alert>
                    <Input
                      id="company-selection-input"
                      type="text"
                      name="company"
                      module="auth"
                      required
                      autoCorrect="off"
                      autoCapitalize="none"
                      inputGroup={
                        <div className="input-group-append">
                          <span className="input-group-text" id="basic-addon2">
                            .merinio.com
                          </span>
                        </div>
                      }
                    />

                    <div className="mb-4 d-flex">
                      <Button id="company-selection-submit" type="submit" isSubmitting={isSubmitting} text="auth.SUBMIT" />
                      <div className="ml-auto">
                        <div>
                          <NavLink to="/forgot/company">{t('auth.FIND_MY_COMPANY')}</NavLink>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </Form>
            )}
          </Formik>
        </Card.Body>
      </MerinioCard>

      {/* Admin debugging code to only show in mobile app */}
      {Capacitor.isNativePlatform() ? (
        <>
          {displayToken > 10 ? (
            <form
              onSubmit={e => {
                e.preventDefault()
                dispatch(setLoggedIn(token))
                setReturnToLogin(true)
              }}
            >
              <textarea value={token} onChange={e => setToken(e.target.value)} required />
              <MerinioButton style={{ height: '50px', marginLeft: '10px' }}>{t('SUBMIT')}</MerinioButton>
            </form>
          ) : null}
          <div
            className="float-right"
            style={{ position: 'fixed', bottom: 0, left: 0, width: '50px', height: '50px' }}
            onClick={() => setDisplayToken(displayToken + 1)}
          ></div>
        </>
      ) : null}
    </>
  )
}

export default SelectCompany
